import React, { useMemo } from "react";

import type {
  ProcessedBetEntry,
  ProcessedEntry,
  ProcessedPickemEntry,
} from "@/hooks/firestore/betting/useEntries";

import cx from "classnames";
import { combinations as calculateCombinations } from "mathjs";
import {
  BetComboIcon as ComboIcon,
  BetMultiIcon as MultiIcon,
  BetQuaddieIcon as QuaddieIcon,
  BetSingleIcon as SingleIcon,
  Caret,
  Clamp,
  Fireworks,
  PickIcon as PickemIcon,
  SGMIcon,
  ShinyText,
  SmallPromoIcon,
  SRMIcon,
} from "@picklebet/ui";
import { useIsParlayLocale } from "@/hooks/useMultiSlug";
import { BetEntryClamp } from "./bets/BetEntryClamp";
import {
  calculateTotalLegsIncludingSubOutcomes,
  getPickemEntryStatus,
  shouldShowPromoIcon,
} from "../util/entry-helpers";
import { getQuaddieTitle } from "@/features/betslip/utils/selectionHelpers";
import type { QuaddieType } from "@/features/betslip/types";
import { getComboLabel } from "utilities/sharedBettingUtilities";
import pluralize from "pluralize";
import { startCase } from "lodash";
import {
  isExoticSelection,
  isPickemsEntry,
  isQuaddieSelection,
  isSameEventMultiSelection,
  isSingleSelection,
} from "../util/type-guards";
import { getLadderLink } from "features/pickems/util/helpers";
import Link from "@/features/shell/components/shared/Link/Link";
import { useTranslation } from "react-i18next";
import useMustHidePickems from "@/hooks/useMustHidePickems";
import { WIN_CELEBRATION_SHINE_TIMING } from "./common/WinCelebration";

import * as styles from "./EntryHeader.module.scss";

interface EntryHeaderProps {
  entry: ProcessedEntry;
  isPreview?: boolean;
}

const EntryHeader = ({ entry, isPreview }: EntryHeaderProps) => {
  if (isPickemsEntry(entry)) {
    return <PickemEntryHeader entry={entry} />;
  }

  const firstSelection = entry.data.selections[0];

  const isCombo = !!entry.data.selectionsRequiredToWin;
  const isSEM = isSameEventMultiSelection(firstSelection);
  const isQuaddie = isQuaddieSelection(firstSelection);
  const isMulti = !isQuaddie && entry.data.selections.length > 1;

  const isRace = firstSelection.event.eventType === "RACE";

  if ((isMulti || isSEM) && !isCombo) {
    return (
      <MultiEntryHeader
        entry={entry}
        isSRM={isSEM && isRace && entry.data.selections.length < 2}
        isSGM={isSEM && !isRace && entry.data.selections.length < 2}
        isPreview={isPreview}
      />
    );
  }

  if (isCombo) {
    return <ComboMultiEntryHeader entry={entry} isPreview={isPreview} />;
  }

  if (isSingleSelection(firstSelection)) {
    return <SingleEntryHeader entry={entry} isPreview={isPreview} />;
  }

  if (isQuaddie) {
    return <QuaddieEntryHeader entry={entry} isPreview={isPreview} />;
  }

  if (isExoticSelection(firstSelection)) {
    return <ExoticEntryHeader entry={entry} isPreview={isPreview} />;
  }

  return null;
};

const HeaderDisplay = ({
  title,
  description,
  icon,
  clamp,
  hideBottomBorder = false,
}: {
  title: React.ReactNode;
  description: string;
  icon: React.ReactNode;
  clamp: React.ReactNode;
  hideBottomBorder?: boolean;
}) => {
  return (
    <div
      className={cx(styles.header, {
        [styles.hideBottomBorder]: hideBottomBorder,
      })}
    >
      <div className={styles.icon}>{icon}</div>

      <div className={styles.titleAndSubtitleWithChildren}>
        <div className={styles.titleAndSubtitle}>
          <div className={styles.titleRow}>
            {clamp}
            <span className={styles.title}>{title}</span>
          </div>
          <div className={styles.subtitle}>{description}</div>
        </div>
      </div>
    </div>
  );
};

const SingleEntryHeader = ({
  entry,
  isPreview,
}: { entry: ProcessedBetEntry; isPreview?: boolean }) => {
  const singleSelection = entry.data.selections[0];
  const isRace = singleSelection.event.eventType === "RACE";

  const showPromo = shouldShowPromoIcon(entry, isPreview);

  const description = isRace
    ? `${singleSelection.event.attributes?.venueName} - R${singleSelection.event.attributes?.raceNumber}`
    : singleSelection.event.eventName ?? "";

  return (
    <HeaderDisplay
      title="Single"
      description={description}
      icon={
        <>
          {showPromo && <SmallPromoIcon className={styles.promoIcon} />}
          <SingleIcon />
        </>
      }
      clamp={<BetEntryClamp entry={entry} />}
    />
  );
};

const MultiEntryHeader = ({
  entry,
  isSRM,
  isSGM,
  isPreview,
}: {
  entry: ProcessedBetEntry;
  isSRM: boolean;
  isSGM: boolean;
  isPreview?: boolean;
}) => {
  const legs = calculateTotalLegsIncludingSubOutcomes(entry);
  const { t } = useTranslation();
  const isParlayLocale = useIsParlayLocale();

  const title = useMemo(() => {
    if (isSGM) return t("Same Game Multi");
    if (isSRM) return t("Same Race Multi");
    return t("Multi");
  }, [isSGM, isSRM, t]);

  const description = `${legs} legs`;

  const showPromo = shouldShowPromoIcon(entry, isPreview);

  return (
    <HeaderDisplay
      title={
        isSGM || isSRM ? (
          <span className={styles.titleWithIcon}>
            <span className={styles.titleText}>{title}</span>
            {isSRM ? (
              <SRMIcon isParlayLocale={isParlayLocale} />
            ) : (
              <SGMIcon isParlayLocale={isParlayLocale} />
            )}
          </span>
        ) : (
          title
        )
      }
      description={description}
      hideBottomBorder
      icon={
        <>
          {showPromo && <SmallPromoIcon className={styles.promoIcon} />}
          <MultiIcon />
        </>
      }
      clamp={<BetEntryClamp entry={entry} />}
    />
  );
};

const ComboMultiEntryHeader = ({
  entry,
  isPreview,
}: { entry: ProcessedBetEntry; isPreview?: boolean }) => {
  const { t } = useTranslation();
  const description = useMemo(() => {
    const combinations = calculateCombinations(
      entry.data.selections.length,
      entry.data.selectionsRequiredToWin ?? 0,
    );

    const legsToWin = entry.data.selectionsRequiredToWin ?? 0;
    const suffix = combinations > 1 ? "Combinations" : "Combination";

    return `${getComboLabel(legsToWin)} / ${combinations} ${suffix}`;
  }, [entry]);

  const showPromo = shouldShowPromoIcon(entry, isPreview);

  return (
    <HeaderDisplay
      title={`Combo ${t("Multi")}`}
      description={description}
      hideBottomBorder
      icon={
        <>
          {showPromo && <SmallPromoIcon className={styles.promoIcon} />}
          <ComboIcon />
        </>
      }
      clamp={<BetEntryClamp entry={entry} />}
    />
  );
};

const QuaddieEntryHeader = ({
  entry,
  isPreview,
}: { entry: ProcessedBetEntry; isPreview?: boolean }) => {
  const title = useMemo(() => {
    const type = entry.data.quaddieMarket?.type;
    if (!type) return "Quaddie";
    return getQuaddieTitle(type as QuaddieType);
  }, [entry]);

  const showPromo = shouldShowPromoIcon(entry, isPreview);

  const description = pluralize(
    "Combo",
    entry.data.totalCombinations ?? 0,
    true,
  );

  return (
    <HeaderDisplay
      title={title}
      description={description}
      icon={
        <>
          {showPromo && <SmallPromoIcon className={styles.promoIcon} />}
          <QuaddieIcon />
        </>
      }
      clamp={<BetEntryClamp entry={entry} />}
    />
  );
};

const ExoticEntryHeader = ({
  entry,
  isPreview,
}: { entry: ProcessedBetEntry; isPreview?: boolean }) => {
  const firstSelection = entry.data.selections[0];

  if (!isExoticSelection(firstSelection)) {
    return null;
  }

  const marketType = Object.values(firstSelection.outcomes)[0]?.marketType;

  const title = `Exotic - ${startCase(marketType?.toLowerCase() ?? "")}`;

  const description = pluralize(
    "Combo",
    isExoticSelection(firstSelection)
      ? firstSelection.exoticCombinations ?? 0
      : 0,
    true,
  );

  const showPromo = shouldShowPromoIcon(entry, isPreview);

  return (
    <HeaderDisplay
      title={title}
      description={description}
      icon={
        <>
          {showPromo && <SmallPromoIcon className={styles.promoIcon} />}
          <MultiIcon />
        </>
      }
      clamp={<BetEntryClamp entry={entry} />}
    />
  );
};

const PickemEntryHeader = ({ entry }: { entry: ProcessedPickemEntry }) => {
  const url = getLadderLink(entry.referenceId);
  const status = getPickemEntryStatus(entry.data.status);
  const isWon = entry.data.status === "WON";
  const isPlaced = entry.data.status === "PLACED";
  const mustHidePickems = useMustHidePickems();

  return (
    <HeaderDisplay
      title="Pick’em"
      description={entry.data.contestTitle ?? "Pick'ems Contest"}
      icon={<PickemIcon />}
      clamp={
        <div className={styles.pickemClampContainer}>
          <div className={styles.clamp}>
            {status.text && (
              <div className={styles.status}>
                <Clamp variant={status.variant}>
                  <Fireworks
                    isEnabled={
                      (isWon || isPlaced) && status.variant === "success"
                    }
                  >
                    <ShinyText
                      isEnabled={isWon && status.variant === "success"}
                      {...WIN_CELEBRATION_SHINE_TIMING}
                    >
                      {status.text}
                    </ShinyText>
                  </Fireworks>
                </Clamp>
              </div>
            )}
            {!mustHidePickems && (
              <Clamp variant="grey" asChild>
                <Link to={url} noStyles>
                  View Contest <Caret className={styles.caret} />
                </Link>
              </Clamp>
            )}
          </div>
        </div>
      }
    />
  );
};

export { EntryHeader };
